import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { ValuationChart } from "../components/ValuationChart";
import { PlayerValueTimeline } from "../components/PlayerValueTimeline";

const ACCENT = ["border-maasai", "border-ochre", "border-kenya-red"];

export function Valuations() {
  const [settings, setSettings] = useState({});
  useSeo({
    title: "Player Valuations — KickStartKenyaFootball",
    description:
      "The combined estimated career peak valuation of Kenyan footballers abroad: $88.4M USD across 54 players in England, Europe and the USA.",
  });
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const v = settings.valuations;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="valuations-page">
      <StageBar stage="history" />
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <p className="overline text-ochre">Stage one — Kenya&rsquo;s football history</p>
        <h1 className="display-1 mt-5 max-w-4xl">{v?.title || "Total Kenyan Player Valuations"}</h1>
        <p className="mt-6 text-sm uppercase tracking-[0.18em] text-ochre">{v?.period}</p>
        <p className="body-copy mt-8 max-w-3xl" data-testid="valuations-estimate-note">
          These figures are researched estimates of career peak market value, compiled from public records and transfer
          market data across twenty-five years of Kenyan professionals abroad. They are indicative of what Kenyan talent
          has been worth to clubs elsewhere, not audited financial accounts — and they are the basis for our argument
          that investment at home would pay.
        </p>
        {v?.coverage ? (
          <p className="mt-6 max-w-3xl border-l-2 border-ochre pl-5 text-sm leading-relaxed text-zinc-400"
            data-testid="valuations-coverage-note">
            {v.coverage}
          </p>
        ) : null}

        <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3" data-testid="valuation-rows">
          {(v?.rows || []).map((row, i) => (
            <Reveal key={row.region} delay={i * 0.08} className={`border-t-2 ${ACCENT[i % 3]} bg-[#0b0b0b] p-8`}>
              <div data-testid={`valuation-${i}`}>
                <p className="font-display text-5xl font-bold leading-none text-white md:text-6xl">{row.value}</p>
                <p className="mt-5 font-display text-lg uppercase tracking-wide text-white">{row.region}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">{row.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-px border border-white/10 bg-maasai p-9 md:p-12" data-testid="valuation-total">
            <p className="text-[0.62rem] font-bold uppercase tracking-[0.28em] text-white/75">Total</p>
            <p className="mt-3 font-display text-6xl font-bold leading-none text-white md:text-8xl">{v?.total}</p>
            <p className="mt-4 text-sm uppercase tracking-[0.18em] text-white/85">{v?.total_label}</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <ValuationChart rows={v?.rows || []} total={v?.total} />
        </Reveal>

        <PlayerValueTimeline />

        <p className="body-copy mt-10 max-w-3xl">{v?.note}</p>

        <div className="mt-14 flex flex-wrap gap-4">
          <a href={`${API}/talent-case.pdf`} className="btn-primary" data-testid="valuations-talent-pdf">
            <Download size={14} /> Download the talent case (PDF)
          </a>
          <Link to="/hall-of-fame" className="btn-ghost" data-testid="valuations-hof-cta">
            Find the stars <ArrowRight size={14} />
          </Link>
          <Link to="/observations" className="btn-ghost">Key observations</Link>
        </div>
      </div>
      <StageClose stage="history" />
    </div>
  );
}

export function Observations() {
  const [settings, setSettings] = useState({});
  useSeo({
    title: "Key Observations — KickStartKenyaFootball",
    description:
      "Nine observations on Kenyan footballers abroad and the untapped potential of talent development, plus the Santos and Sporting Lisbon export model.",
  });
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="observations-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <p className="overline">Analysis</p>
        <p className="eyebrow">Key observations</p>
        <h1 className="display-1 mt-5 max-w-4xl">Key Observations</h1>
        <p className="body-copy mt-7 max-w-2xl">
          On Kenyan footballers abroad and the untapped potential of talent development.
        </p>

        <div className="mt-14 space-y-px" data-testid="observations-list">
          {(settings.observations || []).map(([title, body], i) => (
            <Reveal key={title} delay={0.04}>
              <div
                className={`grid gap-6 border-l-2 ${ACCENT[i % 3]} bg-[#0b0b0b] p-7 md:grid-cols-[80px_1fr] md:p-9`}
                data-testid={`observation-${i + 1}`}
              >
                <span className="font-display text-4xl font-bold leading-none text-white/15">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 className="font-display text-lg font-semibold uppercase leading-tight text-white md:text-xl">{title}</h2>
                  <p className="body-copy mt-4">{body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-10 border-t-2 border-ochre bg-[#0b0b0b] p-9 md:p-12" data-testid="final-thought">
            <p className="overline text-ochre">Final thought</p>
            <h2 className="display-3 mt-5">Kenya could build a billion-dollar football industry</h2>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-zinc-300 md:text-lg">{settings.final_thought}</p>
          </div>
        </Reveal>

        <div className="mt-14 flex flex-wrap gap-4">
          <Link to="/valuations" className="btn-primary" data-testid="observations-val-cta">
            Player valuations <ArrowRight size={14} />
          </Link>
          <Link to="/plans" className="btn-ghost">The recommended steps</Link>
        </div>
      </div>
    </div>
  );
}

export function Legal() {
  const [settings, setSettings] = useState({});
  useSeo({ title: "Legal Waiver and Disclaimer — KickStartKenyaFootball" });
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="legal-page">
      <div className="mx-auto max-w-[860px] px-5 md:px-10">
        <p className="overline">Legal</p>
        <p className="eyebrow">Legal waiver and disclaimer</p>
        <h1 className="display-2 mt-5">Legal Waiver and Disclaimer</h1>
        <div className="mt-10 space-y-6 whitespace-pre-line border-l-2 border-kenya-red bg-[#0b0b0b] p-7 text-sm leading-relaxed text-zinc-400 md:p-9" data-testid="legal-body">
          {settings.legal}
        </div>
      </div>
    </div>
  );
}
